import { Component, OnInit } from '@angular/core';
import { ApiService } from 'src/app/core/services/api.service';
import { PaginationService } from 'src/app/core/services/pagination.service';
import { ToastService } from '../icons/toast-service';

@Component({
  selector: 'app-quality-checks',
  templateUrl: './quality-checks.component.html',
  styleUrls: ['./quality-checks.component.scss'],
  standalone: false
})
export class QualityChecksComponent implements OnInit {
  breadCrumbItems!: Array<{}>;
  
  // Data
  qualityCheckItems: any[] = [];
  allQualityCheckItems: any[] = [];
  materialAcceptances: any[] = [];
  items: any[] = [];
  
  // Filters
  materialAcceptanceId: string = '';
  itemId: string = '';
  fromDate: string = '';
  toDate: string = '';
  
  // Loading
  isLoading = false;
  
  // Pagination
  page: number = 1;
  pageSize: number = 10;
  totalRecords: number = 0;

  // Form state
  showForm = false;
  qualityCheckForm: any = {
    material_acceptance_id: null,
    item_id: null,
    qc_date: '',
    status: 'Passed',
    test_parameters: '',
    observations: '',
    remarks: ''
  };
  selectedItem: any = null;
  isSubmitting = false;

  constructor(
    private api: ApiService,
    public paginationService: PaginationService,
    public toastService: ToastService
  ) { }

  ngOnInit(): void {
    this.breadCrumbItems = [
      { label: 'Quality Management' },
      { label: 'Pending Quality Checks', active: true }
    ];
    
    // Set default date range (last 15 days)
    const today = new Date();
    const fifteenDaysAgo = new Date();
    fifteenDaysAgo.setDate(today.getDate() - 15);
    
    this.toDate = this.formatDate(today);
    this.fromDate = this.formatDate(fifteenDaysAgo);
    
    this.loadMaterialAcceptances();
    this.loadItems();
    this.loadQualityCheckItems();
  }

  formatDate(date: Date): string {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  loadMaterialAcceptances(): void {
    this.api.get('/material_acceptances').subscribe({
      next: (res: any) => {
        if (res.success) {
          this.materialAcceptances = res.data || [];
        }
      },
      error: (err) => console.error('Error loading material acceptances:', err)
    });
  }

  loadItems(): void {
    this.api.get('/items').subscribe({
      next: (res: any) => {
        if (res.success) {
          this.items = res.data || [];
        }
      },
      error: (err) => console.error('Error loading items:', err)
    });
  }

  loadQualityCheckItems(): void {
    this.isLoading = true;
    
    // Build query parameters with server-side pagination
    let queryParams = `from_date=${this.fromDate}&to_date=${this.toDate}&page=${this.page}&limit=${this.pageSize}`;
    
    if (this.materialAcceptanceId) {
      queryParams += `&material_acceptance_id=${this.materialAcceptanceId}`;
    }
    
    if (this.itemId) {
      queryParams += `&item_id=${this.itemId}`;
    }
    
    this.api.get(`/pending_quality_check_items?${queryParams}`).subscribe({
      next: (res: any) => {
        this.isLoading = false;
        if (res.success) {
          this.qualityCheckItems = res.data || [];
          this.totalRecords = res.total || 0;
        }
      },
      error: (err) => {
        this.isLoading = false;
        console.error('Error loading quality check items:', err);
      }
    });
  }

  applyFilters(): void {
    this.page = 1;
    this.loadQualityCheckItems();
  }

  resetFilters(): void {
    this.materialAcceptanceId = '';
    this.itemId = '';
    this.page = 1;
    
    const today = new Date();
    const fifteenDaysAgo = new Date();
    fifteenDaysAgo.setDate(today.getDate() - 15);
    
    this.toDate = this.formatDate(today);
    this.fromDate = this.formatDate(fifteenDaysAgo);
    
    this.loadQualityCheckItems();
  }

  changePage(): void {
    this.loadQualityCheckItems();
  }

  getStatusClass(status: string): string {
    const statusClasses: any = {
      'Pending': 'bg-warning-subtle text-warning',
      'Pending QC': 'bg-warning-subtle text-warning',
      'In Progress': 'bg-info-subtle text-info',
      'Passed': 'bg-success-subtle text-success',
      'Failed': 'bg-danger-subtle text-danger',
      'On Hold': 'bg-secondary-subtle text-secondary'
    };
    return statusClasses[status] || 'bg-secondary-subtle text-secondary';
  }

  openQualityCheckForm(item: any): void {
    this.selectedItem = item;
    this.showForm = true;
    
    // Pre-fill form with item data
    this.qualityCheckForm = {
      material_acceptance_id: item.material_acceptance_id || null,
      item_id: item.item_id || null,
      qc_date: this.formatDate(new Date()),
      status: 'Passed',
      test_parameters: '',
      observations: '',
      remarks: ''
    };
    
    // Scroll to form
    setTimeout(() => {
      document.getElementById('qualityCheckForm')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 100);
  }

  submitQualityCheck(): void {
    if (!this.qualityCheckForm.material_acceptance_id || !this.qualityCheckForm.item_id) {
      this.toastService.show('Material Acceptance ID and Item ID are required', { 
        classname: 'bg-danger text-white', 
        delay: 5000 
      });
      return;
    }

    if (!this.qualityCheckForm.qc_date) {
      this.toastService.show('QC Date is required', { 
        classname: 'bg-danger text-white', 
        delay: 5000 
      });
      return;
    }

    this.isSubmitting = true;
    
    this.api.post('/quality_checks', this.qualityCheckForm).subscribe({
      next: (res: any) => {
        this.isSubmitting = false;
        if (res.success) {
          this.toastService.show('Quality check submitted successfully', { 
            classname: 'bg-success text-white', 
            delay: 5000 
          });
          this.cancelForm();
          this.loadQualityCheckItems(); // Reload the list
        } else {
          this.toastService.show('Error: ' + (res.message || 'Failed to submit quality check'), { 
            classname: 'bg-danger text-white', 
            delay: 5000 
          });
        }
      },
      error: (err) => {
        this.isSubmitting = false;
        console.error('Error submitting quality check:', err);
        this.toastService.show('Error submitting quality check: ' + (err.error?.message || err.message), { 
          classname: 'bg-danger text-white', 
          delay: 5000 
        });
      }
    });
  }

  cancelForm(): void {
    this.showForm = false;
    this.selectedItem = null;
    this.qualityCheckForm = {
      material_acceptance_id: null,
      item_id: null,
      qc_date: '',
      status: 'Passed',
      test_parameters: '',
      observations: '',
      remarks: ''
    };
  }
}
